// The review state on the run doc (editable plan 1a, contract 1.9). Pure: no React,
// no I/O. serializeReviewState is what every reviewState write stores;
// restoreReviewState reads one back. Any org member can rewrite a run doc
// (firestore.rules checks membership only), so restore treats every field as
// untrusted: rows are revalidated, duplicates and unknown values are dropped, and
// fields it does not know are ignored.
import {
  ROW_SERVICES,
  SERVICE_DOMAINS,
  cloneJson,
  normalizeReplayRow,
  validateRowParams,
} from '../ai/rows/rowContract';
import { rowsFromLegacyPlan } from '../ai/rows/legacyRows';
import { BUILTIN_RANKING_WEIGHTS, validRankingWeights } from '../config/ranking';
import { INITIAL_REVIEW_STATE, REVIEW_SCHEMA_VERSION, REVIEW_STAGES } from './reviewReducer';

const RESTORABLE_STAGES = Object.freeze(['understood', 'routed']);
const SKIP_REASONS = Object.freeze(['org_policy', 'zero_weight', 'plan_max']);
const CLASSIFIER_SOURCES = Object.freeze(['chip', 'classifier', 'keyword_fallback']);
const PRESET_SOURCE = /^preset:[A-Za-z0-9_-]{1,64}$/;

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// Firestore rejects an array that directly contains an array. Same rule as
// db.js _removeNestedArrays: a field holding such an array is dropped, and the
// walk goes into objects and into the object elements of arrays.
function withoutNestedArrays(value) {
  if (Array.isArray(value)) return value.map((el) => (isPlainObject(el) ? withoutNestedArrays(el) : el));
  if (!isPlainObject(value)) return value;
  const out = {};
  for (const [key, v] of Object.entries(value)) {
    if (Array.isArray(v) && v.some(Array.isArray)) continue;
    out[key] = withoutNestedArrays(v);
  }
  return out;
}

function classifierSourceOf(value) {
  return CLASSIFIER_SOURCES.includes(value) ? value : null;
}

function rankingSourceOf(value) {
  return value === 'builtin' || (typeof value === 'string' && PRESET_SOURCE.test(value)) ? value : 'builtin';
}

// Each stored row through normalizeReplayRow (live GT4SD ids, contract 1.2.6), then
// rows failing validateRowParams are dropped, then later duplicates by id.
function restoreRows(raw) {
  const rows = [];
  const ids = new Set();
  for (const stored of Array.isArray(raw) ? raw : []) {
    const row = normalizeReplayRow(stored);
    if (!validateRowParams(row).ok || ids.has(row.id)) continue;
    ids.add(row.id);
    rows.push(row);
  }
  return rows;
}

function restoreSkipped(raw) {
  const skipped = [];
  for (const entry of Array.isArray(raw) ? raw : []) {
    if (!isPlainObject(entry) || !ROW_SERVICES.includes(entry.service) || !SKIP_REASONS.includes(entry.reason)) continue;
    const next = {
      service: entry.service,
      domain: SERVICE_DOMAINS[entry.service].includes(entry.domain) ? entry.domain : null,
      reason: entry.reason,
    };
    if (entry.fallback === 'fm4m' || entry.fallback === null) next.fallback = entry.fallback;
    skipped.push(next);
  }
  return skipped;
}

function restoreRouterSnapshot(raw) {
  if (!isPlainObject(raw)) return null;
  return {
    rows: restoreRows(raw.rows),
    skipped: restoreSkipped(raw.skipped),
    reconstructed: raw.reconstructed === true,
  };
}

function restoreUnsteerableTargets(raw) {
  return (Array.isArray(raw) ? raw : [])
    .filter((e) => isPlainObject(e) && Number.isInteger(e.index) && e.index >= 0 && typeof e.name === 'string')
    .map((e) => ({ index: e.index, name: e.name }));
}

/**
 * The reviewState field written to the run doc: schemaVersion plus the reducer's
 * fields, JSON round-tripped (no undefined) and with no array directly inside an
 * array. AppProvider never writes the raw reducer state.
 */
export function serializeReviewState(state) {
  const s = isPlainObject(state) ? state : INITIAL_REVIEW_STATE;
  return withoutNestedArrays(cloneJson({
    schemaVersion: REVIEW_SCHEMA_VERSION,
    stage: REVIEW_STAGES.includes(s.stage) ? s.stage : 'idle',
    decomposerSnapshot: s.decomposerSnapshot ?? null,
    spec: s.spec ?? null,
    routerSnapshot: s.routerSnapshot ?? null,
    rows: Array.isArray(s.rows) ? s.rows : [],
    rankingWeights: s.rankingWeights ?? BUILTIN_RANKING_WEIGHTS,
    proposedRankingWeights: s.proposedRankingWeights ?? BUILTIN_RANKING_WEIGHTS,
    proposedRankingSource: s.proposedRankingSource ?? 'builtin',
    unsteerableTargets: Array.isArray(s.unsteerableTargets) ? s.unsteerableTargets : [],
    classifierSource: s.classifierSource ?? null,
  }));
}

/**
 * A ReviewState from a stored reviewState, or null unless schemaVersion is 1, the
 * stage is 'understood' or 'routed' and spec is an object. Every field is rebuilt
 * from the stored value under the rules in contract 1.9; unknown fields are ignored.
 */
export function restoreReviewState(doc) {
  if (!isPlainObject(doc) || doc.schemaVersion !== REVIEW_SCHEMA_VERSION) return null;
  if (!RESTORABLE_STAGES.includes(doc.stage) || !isPlainObject(doc.spec)) return null;
  return {
    stage: doc.stage,
    decomposerSnapshot: isPlainObject(doc.decomposerSnapshot) ? cloneJson(doc.decomposerSnapshot) : null,
    spec: cloneJson(doc.spec),
    routerSnapshot: restoreRouterSnapshot(doc.routerSnapshot),
    rows: restoreRows(doc.rows),
    rankingWeights: validRankingWeights(doc.rankingWeights) || BUILTIN_RANKING_WEIGHTS,
    proposedRankingWeights: validRankingWeights(doc.proposedRankingWeights) || BUILTIN_RANKING_WEIGHTS,
    proposedRankingSource: rankingSourceOf(doc.proposedRankingSource),
    unsteerableTargets: restoreUnsteerableTargets(doc.unsteerableTargets),
    classifierSource: classifierSourceOf(doc.classifierSource),
  };
}

/**
 * A routed review for a legacy draft: a history row with no reviewState (pre-1a),
 * from Firestore (generationSpec) or from this session's history (plan). Routing
 * runs once under the current policy, plan max and custom models; the draft's own
 * budgetOverrides land on the rows they name, and the snapshot is marked
 * reconstructed (never logged, Part 3). h.plannedSteps is ignored. Returns null when
 * the row carries neither plan nor generationSpec.
 * @param {object} historyRow
 * @param {{ customModels, modelPolicy, planMax }} ctx
 */
export function rebuildLegacyReview(historyRow, { customModels, modelPolicy, planMax } = {}) {
  if (!isPlainObject(historyRow)) return null;
  const h = historyRow;
  let plan = null;
  if (isPlainObject(h.plan)) plan = h.plan;
  else if (isPlainObject(h.generationSpec)) plan = { generation_spec: h.generationSpec };
  if (!plan) return null;
  const budgetOverrides = isPlainObject(h.budgetOverrides) ? h.budgetOverrides : {};
  const { spec, rows, routerSnapshot } = rowsFromLegacyPlan(plan, { customModels, modelPolicy, planMax, budgetOverrides });
  return {
    ...INITIAL_REVIEW_STATE,
    stage: 'routed',
    decomposerSnapshot: cloneJson(spec),
    spec: cloneJson(spec),
    routerSnapshot,
    rows,
    classifierSource: classifierSourceOf(h.classifierResult?.source),
  };
}
